/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer, useState } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U2107ROLES } from '../U2107';
import U2107LogicalUnitCategoryBody from './form/U2107LogicalUnitCategoryBody';
import { U2107LogicalUnitCategoryFormToolBar } from './form/U2107LogicalUnitCategoryFormToolBar';
import U2107LogicalUnitCategoryDeleteDlg from './common/U2107LogicalUnitCategoryDeleteDlg';
import { useU2107Context } from '../U2107Context';
import U2000AuditPanelComponent from '../../u2000Core/components/audit/U2000AuditPanelComponent';
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  createEmptyU2107Category,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  luHandleAppiError,
  type U2107LogicalUnitCategory,
  type U2107LogicalUnitCategorySearchRow,
} from '../U2107Service';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../u2000Core/components/tab/U2000Tab';

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

export interface U2107LogicalUnitCategoryFormExtendedHandlers extends U2000FormStateHandlersReturn<U2107LogicalUnitCategory> {
  addNewCategory: () => void;
  modifyCategory: () => void;
  deleteCategory: () => Promise<void>;
}

const U2107LogicalUnitCategoryForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2107Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U2107LogicalUnitCategory) => {
    const result: Record<string, string> = {};
    const isAdd = !row.id;
    if (isAdd) {
      const code = row.code?.trim().toUpperCase() ?? '';
      if (!code) result['code'] = t('errCodeRequired', { ns: NS });
      else if (code.length > 64) result['code'] = t('errCodeMax', { ns: NS });
      else if (!CODE_PATTERN.test(code)) result['code'] = t('errCodePattern', { ns: NS });
    }
    if (!row.labelFr?.trim()) result['labelFr'] = t('errLabelFrRequired', { ns: NS });
    if (!row.labelEn?.trim()) result['labelEn'] = t('errLabelEnRequired', { ns: NS });
    if (row.sortOrder === null || row.sortOrder === undefined || !Number.isFinite(Number(row.sortOrder))) {
      result['sortOrder'] = t('errSortOrderRequired', { ns: NS });
    }
    return result;
  };

  const formBL: U2000FormSBusinessHandlers<U2107LogicalUnitCategory> = {
    newRecord: () => createEmptyU2107Category(),
    validateForm,
    saveForme: async (row: U2107LogicalUnitCategory, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        formExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },
    cancelForm: () => {
      const old = formExtended.getOldData() as U2107LogicalUnitCategory | null;
      if (old) formExtended.setData({ ...old });
      if (luStateHandlers.getActionOriginFrom() === '/U2107') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) handleGet.getData(row.id);
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => {
      navigate('/U2107');
    },
  };

  const formHandlers = useU2000FormStateHandlers<U2107LogicalUnitCategory>({
    formState,
    formStateDispatch,
    businessHandlers: formBL,
  });

  const formExtended: U2107LogicalUnitCategoryFormExtendedHandlers = {
    ...formHandlers,
    addNewCategory: () => {
      formExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      formExtended.setData(createEmptyU2107Category());
      navigate('/U2107/new', { replace: true });
    },
    modifyCategory: () => {
      formExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteCategory: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U2107LogicalUnitCategorySearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => doSearch(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U2107LogicalUnitCategorySearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U2107LogicalUnitCategory>({
    doGetFn: (rowId: string) => doGetData(u2000AxiosAuthenticated, rowId),
    onSuccess: (row: U2107LogicalUnitCategory) => {
      gridStateHandlers.setDataById(row.id, row);
      formExtended.setData(row);
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([
        { href: '/U2107', label: listTitle },
        { label: `${row.code} — ${row.labelFr}` },
      ]);
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        formExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U2107LogicalUnitCategory>({
    doSaveFn: (row: U2107LogicalUnitCategory, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U2107LogicalUnitCategory, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), row]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        navigate(`/U2107/${row.id}`, { replace: true });
      } else {
        gridStateHandlers.setDataById(row.id, row);
      }
      formExtended.afterSaveData(row);
      formExtended.setData(row);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));
      if (
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY
      ) {
        if (luStateHandlers.getActionOriginFrom() === '/U2107') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        formExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => {
      const data = formExtended.getData();
      return data ? [data] : [];
    },
    doDeleteFn: async (_rowId, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, row.id, buildDeleteBody(row));
    },
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((del) => {
        toast.success(t('success0003', { key: del.code, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U2107', { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      formExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      formExtended.setData(createEmptyU2107Category());
      setLogicalUnitBreadcrumb([
        { href: '/U2107', label: listTitle },
        { label: t('btnAdd', { ns: NS }) },
      ]);
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (formExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    hasRole(U2107ROLES.AUDITS) && {
      id: 'tabAudits',
      label: t('tabAudits', { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={formExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U2107LogicalUnitCategoryAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U2107LogicalUnitCategoryDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />
      <U2000DefaultForm
        actionsBtns={<U2107LogicalUnitCategoryFormToolBar formStateHandlers={formExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={formExtended as any}
      >
        <U2107LogicalUnitCategoryBody formStateHandlers={formExtended as any} t={t} />
        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U2107LogicalUnitCategoryForm;
